// @ts-nocheck
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { startOfWeek, startOfDay, addDays, format, isBefore, endOfDay } from 'date-fns';

// Styled-components for various elements in the scheduler
const Container = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
`;

const Days = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const IconButton = styled.button<{ disabled?: boolean; right?: boolean }>`
  background: none;
  border: none;
  width: 10px;
  @media (min-width: 400px) {
    width: 32px;
  }
  @media (min-width: 768px) {
    width: 60px;
  }
  display: flex;
  justify-content: ${(props) => (props.right ? 'flex-end' : 'flex-start')};
  font-size: 24px;

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  color: ${(props) => (props.disabled ? '#6b7280' : '#000000')};
`;

const WeekDayContainer = styled.div`
  width: 80%;
  display: flex;

  justify-content: space-between;
`;

const DayContainer = styled.div<DayProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${(props) => (props.isDesktop ? '16px' : '8px')};
  border-radius: ${(props) => {
    if (props.isSelected) {
      return '16px';
    } else {
      return '0';
    }
  }};
  width: ${(props) => (props.isDesktop ? '48px' : '15%')};
  opacity: ${(props) => (props.isPast ? 0.5 : 1)};
  position: relative;
`;

// Interfaces for styled-components properties
interface DesktopProps {
  isDesktop: boolean;
}

interface DayProps extends DesktopProps {
  isSelected?: boolean;
  isPast?: boolean;
}

interface DayData {
  dayName: string;
  date: Date;
  formattedDate: string;
  isToday: boolean;
  isPast: boolean;
  isSelected: boolean;
  hasTimeslots: boolean;
}

const isDayOfWeek = (day: string): day is keyof AvailableTimeSlots => {
  return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(
    day
  );
};

interface Props {
  timeslots: AvailableTimeSlots;
  onWeekChange: (startOfWeek: Date, endOfWeek: Date) => Promise<void>;
  selectedDay: Date;
  theme?: PageTheme;
  setSelectedDay: React.Dispatch<React.SetStateAction<Date>>;
}

const WeekView: React.FC<Props> = ({
  timeslots,
  onWeekChange,
  selectedDay,
  setSelectedDay,
  theme
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1200);
  const [currentDate, setCurrentDate] = useState(new Date());

  const Text = styled.span<DayProps>`
    font-size: ${(props) => (props.isDesktop ? '20px' : '16px')};
    font-weight: ${(props) => (props.isSelected ? 'bold' : 'normal')};
    color: ${(props) =>
      props.isSelected ? (theme?.primaryColor ? theme.primaryColor : '#4450ce') : '#000000'};
  `;

  const findFirstAvailableDay = (startOfWeek: Date): Date => {
    for (let i = 0; i < 7; i++) {
      const day = addDays(startOfWeek, i);
      const dayOfWeek = format(day, 'EEEE').toLowerCase();
      if (isDayOfWeek(dayOfWeek) && (timeslots[dayOfWeek] ?? []).length > 0) {
        return day;
      }
    }
    return startOfWeek; // Fallback to the start of the week if no available day is found
  };

  const notifyWeekChange = async (newDate: Date) => {
    const start = startOfWeek(newDate, { weekStartsOn: 0 });
    const end = endOfDay(addDays(start, 6));
    onWeekChange(start, end);
    const firstAvailableDay = findFirstAvailableDay(start);
    setSelectedDay(firstAvailableDay);
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1200);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const startOfCurrentWeek = startOfWeek(currentDate, { weekStartsOn: 0 });
  const startOfThisWeek = startOfWeek(new Date(), { weekStartsOn: 0 });

  const weekData: DayData[] = Array.from({ length: 7 }).map((_, index) => {
    const date = addDays(startOfCurrentWeek, index);
    const dayOfWeek = format(date, 'EEEE').toLowerCase();
    const hasTimeslots = isDayOfWeek(dayOfWeek) && (timeslots[dayOfWeek] ?? []).length > 0;

    return {
      dayName: format(date, 'EEE'),
      date,
      formattedDate: format(date, 'd'),
      isToday: format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'),
      isPast: isBefore(startOfDay(date), startOfDay(new Date())),
      isSelected: format(date, 'yyyy-MM-dd') === format(selectedDay, 'yyyy-MM-dd'),
      hasTimeslots
    };
  });

  const handleDayClick = (day: DayData) => {
    if (day.hasTimeslots && !day.isPast) {
      setSelectedDay(day.date);
    }
  };

  const handlePrevWeek = () => {
    const newDate = addDays(currentDate, -7);
    if (isBefore(startOfWeek(newDate, { weekStartsOn: 0 }), startOfThisWeek)) {
      return;
    }
    setCurrentDate(newDate);
    notifyWeekChange(newDate);
  };

  const handleNextWeek = () => {
    const newDate = addDays(currentDate, 7);
    setCurrentDate(newDate);
    notifyWeekChange(newDate);
  };

  return (
    <Container>
      <Days>
        <IconButton
          onClick={handlePrevWeek}
          disabled={isBefore(addDays(startOfCurrentWeek, -7), startOfThisWeek)}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </IconButton>

        <WeekDayContainer>
          {weekData.map((day, index) => (
            <DayContainer
              key={index}
              isSelected={day.isSelected}
              isDesktop={isDesktop}
              isPast={day.isPast || !day.hasTimeslots}
              onClick={() => handleDayClick(day)}
              style={{
                cursor: day.isPast || !day.hasTimeslots ? 'not-allowed' : 'pointer',
                opacity: day.isPast || !day.hasTimeslots ? 0.2 : 1
              }}
            >
              <Text isSelected={day.isSelected} isDesktop={isDesktop}>
                {day.dayName}
              </Text>
              <Text isSelected={day.isSelected} isDesktop={isDesktop}>
                {day.formattedDate}
              </Text>
            </DayContainer>
          ))}
        </WeekDayContainer>
        <IconButton onClick={handleNextWeek} right>
          <FontAwesomeIcon icon={faChevronRight} />
        </IconButton>
      </Days>
    </Container>
  );
};

export default WeekView;
